import { useMemo } from "react";
import { EventType } from "@/types/event";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { format, addMinutes, isSameDay } from "date-fns";

interface DayViewProps {
  date: Date;
  events: EventType[];
  onSelectTime: (time: Date) => void;
  onSelectEvent: (event: EventType) => void;
  isDemoMode?: boolean;
}

const DayView = ({ date, events, onSelectTime, onSelectEvent, isDemoMode = false }: DayViewProps) => {
  const { toast } = useToast();
  
  // Filter events for this day - improved to handle all event types
  const dayEvents = useMemo(() => {
    return events.filter(event => {
      // For regular events, check if the start date is on the selected day
      const eventStartDate = new Date(event.startDate);
      const eventEndDate = new Date(event.endDate);
      
      // Check if the event occurs on the selected day
      // An event is on this day if:
      // 1. The event starts on this day, OR
      // 2. The event ends on this day, OR
      // 3. The event spans over this day (starts before and ends after)
      return (
        isSameDay(eventStartDate, date) || 
        isSameDay(eventEndDate, date) || 
        (eventStartDate < date && eventEndDate > date)
      );
    });
  }, [date, events]);

  // Hours for the timeline (6AM to 10PM)
  const hours = Array.from({ length: 17 }, (_, i) => i + 6);

  // Handle time slot click to create new event
  const handleTimeSlotClick = (hour: number, minute: number = 0) => {
    // Create a date object with the selected hour and minute
    const selectedTime = new Date(date);
    selectedTime.setHours(hour, minute, 0, 0); // Set seconds and milliseconds to 0
    
    if (isDemoMode) {
      toast({
        title: "Demo Mode",
        description: "This is a demonstration calendar. Sign up for a full account to create events.",
        variant: "default",
      });
      return;
    }
    
    // Pass the selected time to the parent component
    onSelectTime(selectedTime);
  };

  // Format time with proper timezone handling
  const formatEventTime = (timeString: string) => {
    const eventTime = new Date(timeString);
    return format(eventTime, 'h:mm a');
  };

  // Calculate event position and height based on local timezone
  const calculateEventStyles = (event: EventType) => {
    const startTime = new Date(event.startDate);
    const endTime = new Date(event.endDate);
    
    // For events that span multiple days, adjust the display for this specific day
    let displayStartTime = startTime;
    let displayEndTime = endTime;
    
    // If event started before today, set display start to beginning of this day
    if (startTime.toDateString() !== date.toDateString() && startTime < date) {
      displayStartTime = new Date(date);
      displayStartTime.setHours(0, 0, 0, 0);
    }
    
    // If event ends after today, set display end to end of this day
    if (endTime.toDateString() !== date.toDateString() && endTime > date) {
      displayEndTime = new Date(date);
      displayEndTime.setHours(23, 59, 59, 999);
    }
    
    // Calculate top position based on start hour and minute
    const startHour = displayStartTime.getHours();
    const startMinute = displayStartTime.getMinutes();
    const startFromTop = (startHour - 6) * 100 + (startMinute / 60) * 100; // 6 is the first hour shown
    
    // Calculate duration in minutes for this day's portion of the event
    const durationMinutes = (displayEndTime.getTime() - displayStartTime.getTime()) / (60 * 1000);
    const heightPercentage = Math.max(Math.min(durationMinutes / 60 * 100, 100), 5); // Min height 5% for visibility
    
    return {
      top: `${startFromTop}%`,
      height: `${heightPercentage}%`,
      zIndex: 10
    };
  };

  // Group events by hour for better rendering
  const eventsByHour = useMemo(() => {
    const hourMap: Record<number, EventType[]> = {};
    
    // Initialize all hours
    hours.forEach(hour => {
      hourMap[hour] = [];
    });
    
    // Place events in their starting hour bucket
    dayEvents.forEach(event => {
      const startTime = new Date(event.startDate);
      const startHour = startTime.getHours();
      
      // Only add to hours we're displaying
      if (startHour >= 6 && startHour <= 22) {
        hourMap[startHour].push(event);
      }
    });
    
    return hourMap;
  }, [dayEvents, hours]);

  return (
    <div className="flex flex-col h-full overflow-auto">
      <div className="flex min-w-max">
        {/* Time gutter */}
        <div className="w-16 flex-shrink-0">
          <div className="h-12"></div> {/* Empty space for header */}
          {hours.map(hour => (
            <div key={hour} className="h-20 border-t text-xs text-gray-500 pr-2 text-right">
              {hour % 12 === 0 ? '12' : hour % 12}{hour < 12 ? 'am' : 'pm'}
            </div>
          ))}
        </div>
        
        {/* Day column */}
        <div className="flex-1 min-w-max relative">
          {/* Day header */}
          <div className="text-center p-2 border-b font-medium">
            <div className="text-sm">
              {date.toLocaleDateString('en-US', { weekday: 'long' })}
            </div>
            <div className={cn(
              "inline-flex items-center justify-center w-6 h-6 rounded-full text-sm",
              date.toDateString() === new Date().toDateString() ? "bg-blue-100 text-blue-700" : ""
            )}>
              {date.getDate()}
            </div>
          </div>
          
          {/* Time slots */}
          {hours.map(hour => (
            <div 
              key={hour} 
              className={cn(
                "h-20 border-t relative hover:bg-gray-50 transition-colors",
                !isDemoMode && "cursor-pointer"
              )}
            >
              {/* Divide hour into 4 quarters with clickable areas */}
              <div className="grid grid-rows-4 h-full">
                {[0, 15, 30, 45].map(minute => (
                  <div
                    key={minute}
                    className="border-t border-gray-100 first:border-t-0 cursor-pointer"
                    onClick={() => handleTimeSlotClick(hour, minute)}
                  ></div>
                ))}
              </div>
            </div>
          ))}
          
          {/* Render all events as absolute positioned elements */}
          {dayEvents.map((event, i) => {
            const styles = calculateEventStyles(event);
            
            return (
              <div
                key={`event-${event.id}-${i}`}
                className={`absolute left-1 right-1 rounded p-2 ${event.color} cursor-pointer overflow-hidden`}
                style={styles}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectEvent(event);
                }}
              >
                <div className="font-medium truncate">{event.title}</div>
                <div className="text-xs truncate">
                  {formatEventTime(event.startDate)}
                  {" - "}
                  {formatEventTime(event.endDate)}
                </div>
                {event.location && (
                  <div className="text-xs truncate">{event.location}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Debug info - only in development */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mt-4 p-2 bg-gray-100 text-xs">
          <div>Selected date: {date.toISOString()}</div>
          <div>Events for this day: {dayEvents.length}</div>
          <div>Total events: {events.length}</div>
        </div>
      )}
    </div>
  );
};

export default DayView;
